import React, { useEffect, useState } from "react";
import styled from "styled-components";
import ZoomableImage from './ZoomableImage';
import { useModalStore } from '../store/portal/modalStore';
import downloadIcon from '../../asset/icon/download.png';
import deleteIcon from '../../asset/icon/delete.png';
import createIcon from '../../asset/icon/create.png';
import minusIcon from '../../asset/icon/minus.png';
import HintWrapper from "./headless/button/HintWrapper";
import { getHintMessage } from '../constants/hintMessages';
import { handleDownload } from '../util/download';
import { fadeIn, fadeOut } from "./Animation";
import { useDeviceStore } from "../store/deviceStore";
import { DeviceType } from '../enum/EnumRepository';
import { paperTextureBefore } from './paperTexture';
import UniversalIconBtn from './headless/button/UniversalIconBtn';

const ANIMATION_TIME = 700;

const FullImageViewer: React.FC = () => {
  const { standardModal, clearModal, isClosing } = useModalStore();
  const [scale, setScale] = useState(1);
  const [isShiftPressed, setIsShiftPressed] = useState(false);
  const { deviceType } = useDeviceStore();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' || event.key === ' ') {
        clearModal(ANIMATION_TIME);
      }
      if (event.key === 'Shift') {
        setIsShiftPressed(true);
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.key === 'Shift') {
        setIsShiftPressed(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, [clearModal]);


  const handleZoomIn = () => {
    if (scale < 3) {
      setScale(scale + 0.2);
    }
  }

  const handleZoomOut = () => {
    if (scale > 0.6) {
      setScale(scale - 0.2);
    }
  }

  return (
    <FixedOverlay $isClosing={isClosing}>
      <CloseIconBtnWrapper 
        $isMobile={deviceType === DeviceType.MOBILE}
        onClick={() => clearModal(ANIMATION_TIME)}>
        <UniversalIconBtn
          size="standard"
          border={false}
          backgroundImg={deleteIcon}
          onClick={() => clearModal(ANIMATION_TIME)}
        />
      </CloseIconBtnWrapper>
      <BtnContainer>
        <HintWrapper
          content={getHintMessage('ZOOM_IN')}
          content2={getHintMessage('ZOOM_IN_2')}
        >
          <UniversalIconBtn
            size="standard"
            border={true}
            backgroundImg={createIcon}
            onClick={handleZoomIn}
          />
        </HintWrapper>
        <HintWrapper
          content={getHintMessage('ZOOM_OUT')}
          content2={getHintMessage('ZOOM_OUT_2')}
        >
          <UniversalIconBtn
            size="standard"
            border={true}
            backgroundImg={minusIcon}
            onClick={handleZoomOut}
          />
        </HintWrapper>
        {standardModal?.option &&
          <HintWrapper content={getHintMessage('DOWNLOAD_IMAGE')}>
            <UniversalIconBtn
              size="standard"
              border={true}
              backgroundImg={downloadIcon}
              onClick={() => handleDownload(standardModal!.value!, standardModal!.title!)}
            />
          </HintWrapper>
        }
      </BtnContainer>
      <FullImageViewerLayer>
        <ZoomableImage
          imageUrl={standardModal!.value!}
          isShiftPressed={isShiftPressed}
          scale={scale}
          setScale={setScale}
        />
      </FullImageViewerLayer>
    </FixedOverlay>
  );
};

export default FullImageViewer;

const FixedOverlay = styled.div<{ $isClosing: boolean }>`
  position: fixed;
  top: 0;
  left: 0;

  width: 100vw;
  height: 100vh;

  ${paperTextureBefore}
  
  z-index: 5; /* 이미지 뷰어 전체 오버레이: 일반 모달보다 위, 버튼(6)보다는 아래 */
  transition: all 0.7s ease-out;
  
  ${({ $isClosing }) => $isClosing ? fadeOut : fadeIn}
`;


const FullImageViewerLayer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  
  width: 100vw;
  height: 100vh;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  overflow: scroll;
  &::-webkit-scrollbar { width: 3px; height: 3px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: #bfbfbf; border-radius: 1px; }
`;

const CloseIconBtnWrapper = styled.div<{ $isMobile: boolean }>`
  position: fixed;
  top: ${({ $isMobile }) => $isMobile ? 'clamp(18px, 5vw, 24px);' : 'clamp(20px, 2vw, 30px)'};
  right: ${({ $isMobile }) => $isMobile ? 'clamp(18px, 5vw, 24px)' : 'clamp(20px, 2vw, 30px)'};
  z-index: 6; /* 오버레이 위에서 항상 보이는 닫기 버튼 */
`;

const BtnContainer = styled.div`
  position: fixed;
  bottom: 4vh;

  left: 50%;
  transform: translateX(-50%);
  display: flex;
  
  gap: 0.5vh;
  z-index: 6; /* 줌/다운로드 버튼 영역: 이미지 위 최상위 컨트롤 */
`;

